/**
 * The local player's completed quest ids, for canvas code (lane BO).
 *
 * A render-side projection of the authoritative React quest snapshot:
 * GameUIOverlay writes it whenever the snapshot changes, and canvas entities
 * that show quest-dependent text (the quest-gated portal prompt) subscribe.
 * Listeners run only when the set actually changes, so subscribers redraw text
 * on the state change, never per frame. The server stays the authority: this
 * store only decides what a label says.
 */

type Listener = () => void;

export class CompletedQuestStore {
  private ids = new Set<string>();
  private key = '';
  private readonly listeners = new Set<Listener>();

  /** Replace the completed set; notifies only when its contents change. */
  public set(ids: Iterable<string>): void {
    const next = new Set(ids);
    const key = [...next].sort().join('|');
    if (key === this.key) return;
    this.key = key;
    this.ids = next;
    for (const listener of [...this.listeners]) listener();
  }

  public has(id: string): boolean {
    return this.ids.has(id);
  }

  /** Subscribe to changes; returns the unsubscribe function. */
  public subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }
}

export const completedQuestStore = new CompletedQuestStore();

/** Active quests whose objectives are met (not yet turned in). They open sealed
 * portals (shared/logic/portalSeal.ts) but not quest gates. */
export const objectivesDoneQuestStore = new CompletedQuestStore();
